// Extracted from LogsPage.tsx - Device logs tab (original functionality)

import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDataProvider, useTranslate, useGetList, usePermissions, useGetIdentity } from 'react-admin';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  Circle,
  Terminal,
} from '@mui/icons-material';
import { SelfFilterPopover } from '@/components/common/SelfFilterPopover';
import { LoadingState } from '@/components/common/LoadingState';
import type { UserRole } from '@/types/models';
import { localInputToUtcIso } from '@/lib/localDate';
import { deviceLogsErrorKey } from '@/lib/deviceLogsError';

// ─── Types ────────────────────────────────────────────

interface DeviceLog {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  component?: string;
  origin?: string;
  result?: string;
  transactionId?: string;
  firmware?: string;
  deviceId?: string;
  serialNumber?: string;
  stepCount: number;
  steps: string[];
  extraProperties: Record<string, unknown>;
}

interface GroupOption { id: string; name: string; }
interface HotelOption { id: string; name: string; }
interface DispenserOption {
  id: string;
  installationCode: string;
  name?: string;
  hotelName?: string;
  hotelId?: string;
}

// ─── Level helpers ────────────────────────────────────

const LEVEL_COLORS: Record<string, string> = {
  Information: '#1976d2',
  Warning: '#ed6c02',
  Error: '#d32f2f',
  Fatal: '#9c27b0',
  Debug: '#757575',
};

const getLevelColor = (level: string) => LEVEL_COLORS[level] ?? '#757575';
const LEVEL_OPTIONS = ['Information', 'Warning', 'Error', 'Fatal', 'Debug'];
const COUNT_OPTIONS = [25, 50, 100, 200, 500];
// Smaller first page → faster first paint; the operator can raise it per query.
const DEFAULT_COUNT = 25;

// ─── Property formatting helpers ─────────────────────

const SHORT_VALUE_LIMIT = 80;

const isJsonLike = (value: unknown): boolean => {
  const str = String(value).trim();
  return (str.startsWith('{') && str.endsWith('}')) || (str.startsWith('[') && str.endsWith(']'));
};

const tryPrettyJson = (value: unknown): string | null => {
  try {
    const parsed = JSON.parse(String(value));
    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
};

const isHttpProperty = (key: string): boolean => {
  const lower = key.toLowerCase();
  return lower.includes('httprequest') || lower.includes('httpresponse');
};

// ─── Component ────────────────────────────────────────

const DeviceLogsTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const userRole = (permissions as UserRole) || '';
  const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'Admin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  const assignedGroups: string[] = (identity as any)?.assignedGroups || [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showGroupFilter = isSuperAdmin || isMultiGroupAdmin;
  const showHotelFilter = isSuperAdmin || isGroupAdmin;

  const searchParams = new URLSearchParams(location.search);
  const urlDeviceId = searchParams.get('deviceId');

  // Selectors
  const [selectedGroup, setSelectedGroup] = useState<GroupOption | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<HotelOption | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DispenserOption | null>(null);

  // Logs
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Filters
  const [level, setLevel] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [count, setCount] = useState<number>(DEFAULT_COUNT);

  // When arriving with ?deviceId=, fetch that device first
  const preselecting = useRef(false);
  useEffect(() => {
    if (!urlDeviceId || selectedDevice || preselecting.current) return;
    preselecting.current = true;
    dataProvider.getOne('devices', { id: urlDeviceId }).then(({ data }: any) => {
      const device: DispenserOption = {
        id: data.id,
        installationCode: data.installationCode,
        hotelName: data.hotelName,
        hotelId: data.hotelId,
      };
      if (showHotelFilter && data.hotelId) {
        setSelectedHotel({ id: data.hotelId, name: data.hotelName || data.hotelId });
      }
      setSelectedDevice(device);
    }).catch(() => {
      preselecting.current = false;
    });
  }, [urlDeviceId, selectedDevice, dataProvider, showHotelFilter]);

  // Fetch groups
  const groupFilter = isMultiGroupAdmin ? { id: assignedGroups } : {};
  const { data: groups, isLoading: groupsLoading } = useGetList<any>('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: groupFilter,
  }, { enabled: showGroupFilter && !identityLoading });

  const groupOptions: GroupOption[] = (groups ?? []).map((g: any) => ({ id: g.id, name: g.name }));

  // Determine hotel filter
  const hotelListFilter: Record<string, any> = {};
  if (selectedGroup) {
    hotelListFilter.groupId = selectedGroup.id;
  } else if (isGroupAdmin && !isMultiGroupAdmin && identity?.groupId) {
    hotelListFilter.groupId = identity.groupId;
  } else if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
    hotelListFilter.id = identity.hotelId;
  }

  const { data: hotels, isLoading: hotelsLoading } = useGetList<any>('hotels', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelListFilter,
  }, { enabled: !identityLoading && (isSuperAdmin || isGroupAdmin || !showGroupFilter || !!selectedGroup) });

  const hotelOptions: HotelOption[] = (hotels ?? []).map((h: any) => ({ id: h.id, name: h.name }));

  // Fetch devices
  const deviceFilter: Record<string, any> = {};
  if (selectedHotel) {
    deviceFilter.hotelId = selectedHotel.id;
  } else if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
    deviceFilter.hotelId = identity.hotelId;
  }

  const { data: dispensers, isLoading: dispensersLoading } = useGetList<any>('devices', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'installationCode', order: 'ASC' },
    filter: deviceFilter,
  }, { enabled: showHotelFilter ? !!selectedHotel : !identityLoading });

  const dispenserOptions: DispenserOption[] = (dispensers ?? []).map((d: any) => ({
    id: d.id,
    installationCode: d.installationCode,
    name: d.name,
    hotelName: d.hotelName,
    hotelId: d.hotelId,
  }));

  // Load logs
  const fetchLogs = useCallback(async () => {
    if (!selectedDevice) return;
    setLogsLoading(true);
    setLogsError(null);

    try {
      const { data } = await (dataProvider as any).getDeviceLogs(selectedDevice.id, {
        count,
        ...(level && { level }),
        // The controls are in the browser's local time; the server compares UTC instants.
        ...(fromDate && { fromDate: localInputToUtcIso(fromDate) }),
        ...(toDate && { toDate: localInputToUtcIso(toDate, true) }),
      });
      setLogs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      // The server's message is never shown as-is (it is not translated): its code is.
      setLogsError(translate(deviceLogsErrorKey(err)));
      setLogs([]);
    } finally {
      setLogsLoading(false);
      hasFetched.current = true;
    }
  }, [selectedDevice, dataProvider, count, level, fromDate, toDate, translate]);

  useEffect(() => {
    if (selectedDevice) fetchLogs();
  }, [fetchLogs, selectedDevice]);

  const handleGroupChange = (_event: any, value: GroupOption | null) => {
    setSelectedGroup(value);
    setSelectedHotel(null);
    setSelectedDevice(null);
    setLogs([]);
    setLogsError(null);
    hasFetched.current = false;
  };

  const handleHotelChange = (_event: any, value: HotelOption | null) => {
    setSelectedHotel(value);
    setSelectedDevice(null);
    setLogs([]);
    setLogsError(null);
    hasFetched.current = false;
  };

  const handleDeviceChange = (_event: any, value: DispenserOption | null) => {
    setSelectedDevice(value);
    setLogs([]);
    setLogsError(null);
    hasFetched.current = false;
    if (value) {
      navigate(`/logs?deviceId=${value.id}`, { replace: true });
    } else {
      navigate('/logs', { replace: true });
    }
  };

  const activeFilterCount = [selectedGroup, selectedHotel, selectedDevice, level, fromDate, toDate]
    .filter(Boolean).length;

  return (
    <Box>
      {/* ─── Filters (shared header popover) ─── */}
      <SelfFilterPopover
        count={activeFilterCount}
        onRefresh={fetchLogs}
        refreshing={logsLoading}
      >
        {showGroupFilter && (
          <Autocomplete
            options={groupOptions}
            loading={groupsLoading}
            value={selectedGroup}
            onChange={handleGroupChange}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ minWidth: 150 }}
            renderInput={(params) => (
              <TextField {...params} label={translate('resources.logs.selectGroup')} size="small" />
            )}
          />
        )}

        {showHotelFilter && (
          <Autocomplete
            options={hotelOptions}
            loading={hotelsLoading}
            value={selectedHotel}
            onChange={handleHotelChange}
            disabled={showGroupFilter && !selectedGroup}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{ minWidth: 160 }}
            renderInput={(params) => (
              <TextField {...params} label={translate('resources.logs.selectHotel')} size="small" />
            )}
          />
        )}

        <Autocomplete
          options={dispenserOptions}
          loading={dispensersLoading}
          value={selectedDevice}
          onChange={handleDeviceChange}
          disabled={showHotelFilter ? !selectedHotel : false}
          getOptionLabel={(option) => {
            const displayCode = option.name
              ? `${option.installationCode} - ${option.name}`
              : option.installationCode;
            return !showHotelFilter && option.hotelName
              ? `${displayCode} — ${option.hotelName}`
              : displayCode;
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ minWidth: 180 }}
          renderInput={(params) => (
            <TextField {...params} label={translate('resources.logs.selectDevice')} size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {option.name ? `${option.installationCode} - ${option.name}` : option.installationCode}
                </Typography>
                {!showHotelFilter && option.hotelName && (
                  <Typography variant="caption" color="text.secondary">{option.hotelName}</Typography>
                )}
              </Box>
            </li>
          )}
        />

        {selectedDevice && (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{translate('resources.logs.level')}</InputLabel>
              <Select value={level} label={translate('resources.logs.level')} onChange={(e) => setLevel(e.target.value)}>
                <MenuItem value=""><em>{translate('resources.logs.allLevels')}</em></MenuItem>
                {LEVEL_OPTIONS.map((l) => (
                  <MenuItem key={l} value={l}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Circle sx={{ fontSize: 10, color: getLevelColor(l) }} />
                      {l}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small" type="datetime-local"
              label={translate('resources.logs.from')}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }}
            />
            <TextField
              size="small" type="datetime-local"
              label={translate('resources.logs.to')}
              value={toDate} onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 190 }}
            />

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>{translate('resources.logs.count')}</InputLabel>
              <Select value={count} label={translate('resources.logs.count')} onChange={(e) => setCount(Number(e.target.value))}>
                {COUNT_OPTIONS.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
              </Select>
            </FormControl>
          </>
        )}
      </SelfFilterPopover>

      {/* ─── States ─── */}
      {/* Initial load: the picker data (identity / groups / hotels / devices) is
          still arriving — show clear feedback instead of a dead screen. */}
      {!selectedDevice && (identityLoading || groupsLoading || hotelsLoading || dispensersLoading) && (
        <LoadingState variant="circular" message={translate('resources.logs.loadingInitial')} />
      )}

      {!selectedDevice && !identityLoading && !groupsLoading && !hotelsLoading && !dispensersLoading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Terminal sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">{translate('resources.logs.noDeviceSelected')}</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>{translate('resources.logs.noDeviceSelectedHint')}</Typography>
        </Box>
      )}

      {logsLoading && (
        <LoadingState variant="circular" message={translate('resources.logs.loading')} />
      )}

      {logsError && !logsLoading && <Alert severity="error" sx={{ mb: 2 }}>{logsError}</Alert>}

      {selectedDevice && !logsLoading && !logsError && logs.length === 0 && hasFetched.current && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Terminal sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">{translate('resources.logs.noLogs')}</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>{translate('resources.logs.noLogsHint')}</Typography>
        </Box>
      )}

      {!logsLoading && logs.map((log, index) => (
        <DeviceLogEntry key={log.id || index} log={log} translate={translate} />
      ))}
    </Box>
  );
};

// ─── Property list with smart formatting ─────────────

const PropertyList = memo(({ properties }: { properties: Record<string, unknown> }) => {
  const entries = Object.entries(properties);
  const chipEntries: [string, unknown][] = [];
  const blockEntries: [string, unknown][] = [];

  entries.forEach(([key, value]) => {
    const strValue = String(value);
    if (strValue.length > SHORT_VALUE_LIMIT || isJsonLike(value) || isHttpProperty(key)) {
      blockEntries.push([key, value]);
    } else {
      chipEntries.push([key, value]);
    }
  });

  return (
    <Box>
      {chipEntries.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: blockEntries.length > 0 ? 1.5 : 0 }}>
          {chipEntries.map(([key, value]) => (
            <Chip
              key={key}
              label={`${key}: ${String(value)}`}
              size="small"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: 'grey.100', maxWidth: '100%' }}
            />
          ))}
        </Box>
      )}

      {blockEntries.map(([key, value]) => {
        const prettyJson = tryPrettyJson(value);
        const httpProp = isHttpProperty(key);

        return (
          <Box key={key} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                display: 'block',
                mb: 0.5,
                color: httpProp ? 'primary.main' : 'text.secondary',
              }}
            >
              {httpProp ? `🌐 ${key}` : key}
            </Typography>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                whiteSpace: prettyJson ? 'pre' : 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                bgcolor: httpProp ? 'action.hover' : 'grey.50',
                borderRadius: 1,
                p: 1.5,
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid',
                borderColor: httpProp ? 'primary.light' : 'divider',
              }}
            >
              {prettyJson || String(value)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});
PropertyList.displayName = 'PropertyList';

// ─── Device log entry accordion ──────────────────────

// Memoized: log objects are stable between renders, so collapsed entries skip
// re-rendering entirely while filters/pickers update around them.
const DeviceLogEntry = memo(({ log, translate }: { log: DeviceLog; translate: ReturnType<typeof useTranslate> }) => {
  const ts = new Date(log.timestamp);
  const timeStr = ts.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = ts.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const levelColor = getLevelColor(log.level);
  const hasDetails = log.steps.length > 0 || Object.keys(log.extraProperties).length > 0;

  const metaItems = [
    log.component && { label: translate('resources.logs.component'), value: log.component },
    log.origin && { label: translate('resources.logs.origin'), value: log.origin },
    log.firmware && { label: translate('resources.logs.firmware'), value: log.firmware },
    log.result && { label: translate('resources.logs.result'), value: log.result },
    log.transactionId && { label: translate('resources.logs.transaction'), value: log.transactionId },
    log.serialNumber && { label: translate('resources.logs.serialNumber'), value: log.serialNumber },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <Accordion
      disableGutters elevation={0}
      // Defer the (potentially heavy) details subtree until the entry is expanded,
      // and drop it again on collapse — keeps first paint cheap for long lists.
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        mb: 0.5, '&:before': { display: 'none' },
        border: '1px solid', borderColor: 'divider',
        borderLeftColor: levelColor, borderLeftWidth: 3,
      }}
    >
      <AccordionSummary
        expandIcon={hasDetails ? <ExpandMore /> : undefined}
        sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 0.5 } }}
      >
        <Circle sx={{ fontSize: 10, color: levelColor, flexShrink: 0 }} />
        <Chip label={log.level} size="small" sx={{ backgroundColor: `${levelColor}18`, color: levelColor, fontWeight: 600, fontSize: '0.7rem', height: 22, minWidth: 80 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', flexShrink: 0 }}>{dateStr} {timeStr}</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.message}</Typography>
      </AccordionSummary>

      {hasDetails && (
        <AccordionDetails sx={{ pt: 0, pb: 1.5, pl: 4 }}>
          {log.steps.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                {translate('resources.logs.steps')} ({log.stepCount})
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.6, bgcolor: 'grey.50', borderRadius: 1, p: 1.5, maxHeight: 300, overflow: 'auto' }}>
                {log.steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.disabled', flexShrink: 0, minWidth: 20, textAlign: 'right' }}>{i + 1}.</Typography>
                    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>{step}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {metaItems.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {metaItems.map((item) => (
                <Chip key={item.label} label={`${item.label}: ${item.value}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }} />
              ))}
            </Box>
          )}

          {Object.keys(log.extraProperties).length > 0 && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>{translate('resources.logs.properties')}</Typography>
              <PropertyList properties={log.extraProperties} />
            </Box>
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
});
DeviceLogEntry.displayName = 'DeviceLogEntry';

export default DeviceLogsTab;
